import deckJson from '../content/amarkosha-fpp/deck.json';

export type Gender = 'm' | 'f' | 'n';
export type SymbolKind = 'animal' | 'bird' | 'critter' | 'plant' | 'object';

export interface FppSymbol {
  id: number;
  word: string;
  hindi: string;
  dhatu: string;
  dhatu_artha: string;
  nirukta: string;
  verse: string;
  shloka_ref: string;
  gender: Gender;
  kind: SymbolKind;
  symbol: string;
}

export interface FppCard {
  id: number;
  words: string[];
}

export interface FppDeck {
  deck_name: string;
  symbols_per_card: number;
  total_cards: number;
  symbols: Record<string, FppSymbol>;
  cards: FppCard[];
}

export const FPP_DECK = deckJson as FppDeck;

export function sharedSymbol(a: string[], b: string[]): string | null {
  const other = new Set(b);
  const hits = a.filter((word) => other.has(word));
  return hits.length === 1 ? hits[0] : null;
}

export function assertUniqueMatches(cards: FppCard[]): void {
  for (let i = 0; i < cards.length; i += 1) {
    if (cards[i].words.length !== 9) {
      throw new Error(`Card ${cards[i].id} has ${cards[i].words.length} symbols`);
    }
    for (let j = i + 1; j < cards.length; j += 1) {
      const hit = sharedSymbol(cards[i].words, cards[j].words);
      if (!hit) {
        throw new Error(`Cards ${cards[i].id} and ${cards[j].id} do not share exactly one symbol`);
      }
    }
  }
}

export function shuffleInPlace<T>(items: T[], rng: () => number = Math.random): T[] {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = items[i];
    items[i] = items[j];
    items[j] = tmp;
  }
  return items;
}

export function identificationSentence(word: string, gender: Gender): string {
  if (gender === 'f') return `एषा ${word} अस्ति।`;
  if (gender === 'n') return `एतत् ${word} अस्ति।`;
  return `एषः ${word} अस्ति।`;
}

export function actionSentences(word: string, kind: SymbolKind): string[] {
  if (kind === 'animal' || kind === 'bird' || kind === 'critter') {
    return [`${word} गच्छति।`, `${word} धावति।`, `${word} पश्यति।`];
  }
  if (kind === 'plant') {
    return [`${word} विकसति।`, `${word} सुन्दरम् अस्ति।`];
  }
  return [`अत्र ${word} अस्ति।`, `मम समीपे ${word} अस्ति।`];
}

export function sentenceOptions(symbol: FppSymbol): string[] {
  const unique = new Set([
    identificationSentence(symbol.word, symbol.gender),
    ...actionSentences(symbol.word, symbol.kind),
  ]);
  return [...unique];
}

export function normalizeWord(value: string): string {
  return value.normalize('NFC').replace(/[\s।.!?,-]+/g, '').trim();
}

export function wordMatches(spoken: string, expected: string): boolean {
  const a = normalizeWord(spoken);
  const b = normalizeWord(expected);
  return a.length > 0 && (a === b || a.includes(b) || b.includes(a));
}

export interface PlayerState {
  id: number;
  pile: number[];
}

export interface MatchState {
  players: PlayerState[];
  center: number[];
  activePlayer: number;
}

export function dealMatch(playerCount: number, cards: FppCard[] = FPP_DECK.cards): MatchState {
  const n = Math.min(8, Math.max(1, Math.floor(playerCount)));
  const ids = shuffleInPlace(cards.map((card) => card.id));
  const players: PlayerState[] = [];
  for (let i = 0; i < n; i += 1) {
    const id = ids.pop();
    if (id === undefined) throw new Error('Deck too small');
    players.push({ id: i + 1, pile: [id] });
  }
  return { players, center: ids, activePlayer: 0 };
}

export function cardById(id: number, cards: FppCard[] = FPP_DECK.cards): FppCard {
  const card = cards.find((item) => item.id === id);
  if (!card) throw new Error(`Unknown card ${id}`);
  return card;
}

export function topCard(pile: number[]): number | undefined {
  return pile[pile.length - 1];
}

export function applyClaim(state: MatchState, playerIndex: number): MatchState {
  if (state.center.length === 0) return state;
  const center = state.center.slice(0, -1);
  const won = state.center[state.center.length - 1];
  const players = state.players.map((player, index) => (
    index === playerIndex
      ? { ...player, pile: [...player.pile, won] }
      : player
  ));
  return { ...state, center, players, activePlayer: playerIndex };
}
